import { useState } from 'react';
import Card from './Card';
import { titleCase } from '../lib/format';
import { buildPivot, pivotCsv, downloadCsv, isMoneyMetric } from '../lib/pivot';

// The date × service · SKU pivot, shared by the superadmin Cost & Billing page
// (live provider rows) and the client Billing page (stored rows). Both feed it
// the same row shape.
//
// The metric buttons are a tab strip, not a relabelling: money and usage are
// two different tables. Money splits its columns by currency and totals per
// currency; usage splits by unit and shows no currency at all. lib/pivot builds
// both models and hands back one shape, so the markup below never branches on
// the metric.
//
// rows: [{ date, service, skuId, skuName, unit, currency, quantity, cost, billing }]
// metrics: which of 'cost' | 'billing' | 'usage' to offer, in display order;
//          the first is the default.

export default function UsagePivot({
  rows = [],
  title,
  metrics = ['cost', 'usage'],
  fileBase = 'usage',
  fallbackCurrency = 'USD',
  emptyMessage = 'No dated usage for this period.',
}) {
  const [metric, setMetric] = useState(metrics[0]);
  // Guard against a metric that is no longer offered (e.g. billing disappears
  // when the selected group has no priced rows).
  const metricShown = metrics.includes(metric) ? metric : metrics[0];

  const pivot = buildPivot(rows, { fallbackCurrency });
  const view = pivot.viewFor(metricShown);
  const { dates } = pivot;

  // The heading names the table you are looking at, since the two tabs measure
  // different things.
  const heading = title || `${titleCase(metricShown)} by date × service · SKU`;

  const exportCsv = (m) =>
    downloadCsv(pivotCsv(pivot.viewFor(m), dates), `${fileBase}-${m}.csv`);

  return (
    <Card
      className="card--quiet-title"
      title={heading}
      action={
        <div className="usage-export">
          <div className="seg" role="tablist" aria-label="What the table shows">
            {metrics.map((m) => (
              <button
                key={m}
                type="button"
                role="tab"
                className={`seg__btn ${metricShown === m ? 'seg__btn--on' : ''}`}
                onClick={() => setMetric(m)}
                aria-selected={metricShown === m}
                title={
                  isMoneyMetric(m)
                    ? `${titleCase(m)} per day, split by currency`
                    : 'Quantities per day, split by unit'
                }
              >
                {titleCase(m)}
              </button>
            ))}
          </div>
          {metrics.map((m) => (
            <button
              key={m}
              className="btn btn--ghost btn--sm"
              onClick={() => exportCsv(m)}
              disabled={dates.length === 0}
              title={`Download the ${m} table as CSV (';' separated)`}
            >
              ⭳ {titleCase(m)} CSV
            </button>
          ))}
        </div>
      }
    >
      {dates.length === 0 ? (
        <p className="listview__desc">{emptyMessage}</p>
      ) : (
        <div className="table-wrap">
          <table className="table table--dense table--pivot">
            <thead>
              <tr>
                <th>Date</th>
                {view.cols.map((key) => (
                  <th key={key} className="num">{view.label(key)}</th>
                ))}
                {view.rowTotals
                  ? view.totalKeys.map((c) => (
                      <th key={c} className="num">{view.totalLabel(c)}</th>
                    ))
                  : null}
              </tr>
            </thead>
            <tbody>
              {dates.map((d) => (
                <tr key={d}>
                  <td className="cell-primary">{d}</td>
                  {view.cols.map((key) => (
                    <td key={key} className="num">
                      {view.value(d, key) ? view.text(view.value(d, key), key) : '—'}
                    </td>
                  ))}
                  {view.rowTotals
                    ? view.totalKeys.map((c) => (
                        <td key={c} className="num cell-total">
                          {view.totalText(view.rowTotal(d, c), c)}
                        </td>
                      ))
                    : null}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="cell-primary">Total</td>
                {view.cols.map((key) => (
                  <td key={key} className="num cell-total">
                    {view.text(view.columnTotal(key), key)}
                  </td>
                ))}
                {view.rowTotals
                  ? view.totalKeys.map((c) => (
                      <td key={c} className="num cell-total">
                        {view.totalText(view.grandTotal(c), c)}
                      </td>
                    ))
                  : null}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Card>
  );
}
